/**
 * @file        src/pages/flight/DiscountList.jsx
 * @description B2C Flight Discount list + CRUD over flight_discounts. Mirrors the
 *              legacy console: columns ID / Update Date / Status / Airline Type /
 *              Airline Name / Amount / Action, and an "Add B2C Discount" form of
 *              Discount (%) · Airline Type (All / Airline Wise) · Airline.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (SimpleLookupPage CRUD).
 *   - 2026-07-09 Claude — Dedicated airline-scoped discount UI (parity w/ legacy).
 *   - 2026-08-03 Claude — Required Brand field (registry code, no default) in the
 *                          add/edit modal + Brand column — the API now demands
 *                          `brand` on create.
 *   - 2026-08-10 Claude — Airline Type + Airline pickers now use the themed
 *                          Select (console-wide dropdown consistency pass)
 *                          instead of native <select> (the airline picker was
 *                          a size=6 listbox — now a searchable popup like
 *                          every other dropdown in the console).
 *   - 2026-08-10 Claude — Added "Applies To" (All/B2C/B2B — `applicable_role`,
 *                          added to the table by migration 026 but never
 *                          exposed here) plus title/subtitle/audience props so
 *                          CorporateDiscountList.jsx can reuse this page fixed
 *                          to audience=b2b instead of forking it.
 *   - 2026-09-12 Claude — Removed the "Applies To" input and gave the modal the
 *                          page's `audience`. The modal was never passed one, so
 *                          a NEW row always defaulted to 'all' on BOTH screens —
 *                          a discount created on the Corporate list also applied
 *                          to retail, and vice versa. The screen now decides it
 *                          (this page b2c, CorporateDiscountList.jsx b2b).
 *   - 2026-08-10 Claude — Added Discount Type (Percentage/Flat) so a flight
 *                          discount can be a flat ₹ amount, matching hotel
 *                          discounts (`AddDiscount.jsx`) — the column and the
 *                          DB already supported `flat`, only this form was
 *                          hardcoded to percentage.
 *   - 2026-08-10 Claude — Picking an airline now echoes its name into the
 *                          filter box above the dropdown — previously the box
 *                          kept showing whatever the admin last typed, so the
 *                          selection wasn't visible once the dropdown closed.
 *   - 2026-09-15 Claude — API errors read via apiErrorMessage(): the error handler returns {error:{code,message}}, and putting that object into state/toast threw "Objects are not valid as a React child", blanking the whole app.
 *   - 2026-09-15 Claude — Discount Value is now bounded on BOTH sides by the
 *                          shared rule (lib/discountRules.js): > 0, and <= 100
 *                          when Discount Type is Percentage. The form only
 *                          checked "> 0", so "500%" saved cleanly and was then
 *                          silently clamped to the fare at checkout. The Max
 *                          hint on the input tracks the selected type.
 *   - 2026-09-15 Claude — Added the one-line note stating HOW a discount reaches
 *                          the bill: automatically, best-value rule only, off
 *                          Fare + Taxes + Markup + Add-ons + Convenience fee,
 *                          stacking with a coupon. The screen showed the rules
 *                          but never the base they apply to.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 *   - 2026-10-05 Claude — Fare Type (migration 074), beside the existing Airline
 *                          Type / Airline, so a discount carries the same three
 *                          scope fields as a Flight Markup. Blank = every fare.
 *   - 2026-10-06 Claude — Domestic / International (migration 076): a form field
 *                          and a "Dom / Int" column. Blank = both.
 */
import { useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import BrandSelect from '../../components/BrandSelect';
import Select from '../../components/Select';
import { useBrands } from '../../api/brands';
import { get, post, patch, del, apiErrorMessage } from '../../api/client';
import { confirm } from '../../lib/confirm';
import { fmtDate } from './ReadOnlyListPage';
import { discountValueError, discountValueMax } from '../../lib/discountRules';
import { FLIGHT_DISCOUNT_NOTE } from '../../lib/pricingNotes';
import { FLIGHT_FARE_TYPES } from '../../lib/fareTypes';
import { SelectOrText } from '../package/SimpleLookupPage';
import { DOM_INT_SELECT_OPTIONS, domIntLabel } from '../../lib/domInt';

// The Fare Type picker — the Flight Markup screen's list, plus "Other…".
const FARE_TYPE_FIELD = {
  options: FLIGHT_FARE_TYPES, allowOther: true, maxLength: 30,
  otherPlaceholder: 'Fare type as shown on the site, e.g. Xpress Lite',
};
const fareTypeLabel = (v) => (v ? (FLIGHT_FARE_TYPES.find((o) => o.value === v)?.label || v) : 'All');

const TYPE_LABEL = { all: 'All', airline_wise: 'Airline Wise' };
const AIRLINE_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'airline_wise', label: 'Airline Wise' },
];
const ROLE_LABEL = { all: 'All (legacy)', b2c: 'B2C', b2b: 'B2B' };

export default function DiscountList({
  title = 'Discount List',
  subtitle = 'B2C Flight · airline-wise discounts',
  audience = 'b2c',
} = {}) {
  const qc = useQueryClient();
  const { labelOf } = useBrands();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [modal, setModal] = useState(null); // null | { mode, row? }

  const { data, isLoading } = useQuery({
    queryKey: ['flight-discounts', 'list', { page, pageSize, audience }],
    queryFn: () => get('/flight-discounts', { params: { page, pageSize, audience } }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  const delMut = useMutation({
    mutationFn: (id) => del(`/flight-discounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flight-discounts'] }),
  });

  return (
    <>
      <PageHead title={title} subtitle={subtitle} />

      <div className="notice-banner info">{FLIGHT_DISCOUNT_NOTE}</div>

      <article className="panel glass">
        <div className="search-row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn primary" onClick={() => setModal({ mode: 'create' })}>
            <Icon name="plus" /> Add Discount
          </button>
        </div>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th><th>Brand</th><th>Update Date</th><th>Status</th>
                  <th>Airline Type</th><th>Airline Name</th><th>Fare Type</th><th>Dom / Int</th><th>Amount</th><th>Applies To</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={11} className="empty">No discounts yet.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">#{r.id}</td>
                    <td>{labelOf(r.brand)}</td>
                    <td>{fmtDate(r.updated_at)}</td>
                    <td><Chip tone={r.is_active ? 'ok' : 'rej'}>{r.is_active ? 'Active' : 'Inactive'}</Chip></td>
                    <td>{TYPE_LABEL[r.airline_type] || 'All'}</td>
                    <td>{r.airline_type === 'airline_wise' ? (r.airline_name || r.airline_code || '—') : 'All Airlines'}</td>
                    <td>{fareTypeLabel(r.fare_type)}</td>
                    <td>{domIntLabel(r.dom_int)}</td>
                    <td><b>{Number(r.discount_value)}{r.discount_type === 'flat' ? ' ₹' : ' %'}</b></td>
                    <td>{ROLE_LABEL[r.applicable_role] || '—'}</td>
                    <td>
                      <div className="row-actions">
                        <button className="btn xs ghost" onClick={() => setModal({ mode: 'edit', row: r })}>
                          <Icon name="edit" /> Edit
                        </button>
                        <button className="btn xs danger" onClick={async () => { if (await confirm('Delete this discount?')) delMut.mutate(r.id); }}>
                          <Icon name="trash" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>

      {modal ? <DiscountModal state={modal} audience={audience} onClose={() => setModal(null)} /> : null}
    </>
  );
}

function DiscountModal({ state, audience = 'b2c', onClose }) {
  const qc = useQueryClient();
  const editing = state.mode === 'edit';
  const row = state.row || {};
  const [form, setForm] = useState({
    brand: row.brand || '',
    discount_type: row.discount_type || 'percentage',
    discount_value: row.discount_value ?? '',
    airline_type: row.airline_type || 'all',
    airline_code: row.airline_code || '',
    fare_type: row.fare_type || '',
    dom_int: row.dom_int || '',
    // Fixed by the screen, not chosen. A NEW row takes this page's audience;
    // an existing row keeps its OWN role, so editing a shared 'All' discount
    // from either screen never silently re-scopes it.
    applicable_role: row.applicable_role || (audience === 'b2b' ? 'b2b' : 'b2c'),
    is_active: row.is_active ?? 1,
  });
  const [filter, setFilter] = useState('');
  const [err, setErr] = useState('');

  const { data: airData } = useQuery({
    queryKey: ['flight-airlines'],
    queryFn: () => get('/flight/airlines'),
    staleTime: 5 * 60 * 1000,
    enabled: form.airline_type === 'airline_wise',
  });
  const airlines = airData?.data || [];
  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const list = f ? airlines.filter((a) => `${a.code} ${a.name}`.toLowerCase().includes(f)) : airlines;
    return list.slice(0, 300);
  }, [airlines, filter]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = useMutation({
    mutationFn: () => {
      const isAll = form.airline_type === 'all';
      const air = isAll ? null : airlines.find((a) => a.code === form.airline_code);
      const value = Number(form.discount_value);
      const unit = form.discount_type === 'flat' ? '₹' : '%';
      const body = {
        name: `${value}${unit} · ${isAll ? 'All Airlines' : (air?.name || form.airline_code || 'Airline')}`,
        brand: form.brand,
        discount_type: form.discount_type,
        discount_value: value,
        airline_type: form.airline_type,
        airline_code: isAll ? null : (form.airline_code || null),
        airline_name: isAll ? null : (air?.name || null),
        // Explicit null, so clearing it on edit really clears it.
        fare_type: form.fare_type || null,
        // Explicit null = both, so choosing All on edit clears a stored scope.
        dom_int: form.dom_int || null,
        applicable_role: form.applicable_role,
        is_active: Number(form.is_active) ? true : false,
      };
      return editing ? patch(`/flight-discounts/${row.id}`, body) : post('/flight-discounts', body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['flight-discounts'] }); onClose(); },
    onError: (e) => setErr(apiErrorMessage(e, 'Save failed.')),
  });

  const onSubmit = (e) => {
    e.preventDefault(); setErr('');
    if (!form.brand) { setErr('Select a brand.'); return; }
    const valueErr = discountValueError(form.discount_type, form.discount_value);
    if (valueErr) { setErr(valueErr); return; }
    if (form.airline_type === 'airline_wise' && !form.airline_code) { setErr('Select an airline.'); return; }
    save.mutate();
  };

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 560 }}>
        <div className="lk-modal-head">
          <h3>{editing ? 'Edit B2C Discount' : 'Add B2C Discount'}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>
        <form className="form-grid" onSubmit={onSubmit}>
          <div className="field">
            <label>Brand <span className="req">*</span></label>
            <BrandSelect value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} />
          </div>
          <div className="field">
            <label>Discount Type <span className="req">*</span></label>
            <Select
              value={form.discount_type}
              onChange={(v) => setForm({ ...form, discount_type: v })}
              options={[{ value: 'percentage', label: 'Percentage' }, { value: 'flat', label: 'Flat' }]}
            />
          </div>
          <div className="field">
            <label>Discount Value <span className="req">*</span></label>
            <input type="number" min="0.01" max={discountValueMax(form.discount_type)} step="0.01" value={form.discount_value} onChange={set('discount_value')} placeholder={form.discount_type === 'flat' ? 'e.g. 500' : 'e.g. 10'} />
          </div>
          <div className="field">
            <label>Airline Type</label>
            <Select
              value={form.airline_type}
              onChange={(v) => setForm({ ...form, airline_type: v, airline_code: '' })}
              options={AIRLINE_TYPE_OPTIONS}
            />
          </div>

          {form.airline_type === 'airline_wise' ? (
            <div className="field full">
              <label>Airline <span className="req">*</span></label>
              <input placeholder="Filter airlines…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ marginBottom: 6 }} />
              <Select
                value={form.airline_code}
                onChange={(v) => {
                  setForm({ ...form, airline_code: v });
                  const picked = filtered.find((a) => a.code === v) || airlines.find((a) => a.code === v);
                  if (picked) setFilter(`${picked.name} (${picked.code})`);
                }}
                placeholder="— Select airline —"
                options={filtered.map((a) => ({ value: a.code, label: `${a.name} (${a.code})` }))}
              />
              {airlines.length > 300 && filtered.length === 300 ? (
                <div className="muted" style={{ fontSize: '.78em' }}>Showing first 300 — type to narrow.</div>
              ) : null}
            </div>
          ) : (
            <div className="field">
              <label>Airline</label>
              <input value="All Airline" disabled />
            </div>
          )}

          <div className="field">
            <label>Fare Type</label>
            <SelectOrText f={FARE_TYPE_FIELD} value={form.fare_type} onChange={(v) => setForm({ ...form, fare_type: v })} />
          </div>

          <div className="field">
            <label>Domestic / International</label>
            <Select
              value={form.dom_int}
              onChange={(v) => setForm({ ...form, dom_int: v })}
              options={DOM_INT_SELECT_OPTIONS}
            />
          </div>

          <div className="field">
            <label>Status</label>
            <label className="check">
              <input type="checkbox" checked={!!Number(form.is_active)} onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} />
              <span>Active</span>
            </label>
          </div>

          {err ? <div className="field-error full">{err}</div> : null}
          <div className="form-actions full">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn primary" type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
